import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Activity,
  Loader2,
  Clock9,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Pencil,
  Trash2,
  Building2,
  Hash,
  ShieldCheck,
  UserCheck
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import styles from './ViewOTAttendance.module.css';
import OTAttendanceDrawer from './OTAttendanceDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import NotFound from '../../../pages/Error/NotFound';


const MySwal = withReactContent(Swal);

const ViewOTAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchOTRecord = async () => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      const mockData = {
        id: id,
        employee: 'Michael Chen',
        employee_id: 101,
        employee_code: 'EMP-2024-001',
        project_site: 'Downtown Plaza',
        project_site_id: 1,
        date: '2024-04-30',
        ot_punch_in: '18:30:00',
        ot_punch_out: '21:45:10',
        ot_hours: '3.25',
        status: 'approved',
        department: 'Operations',
        assigned_by: 'Admin User',
        reason: 'Emergency cable fault repair on 4th floor.',
        verified_by: 'Project Manager',
        verification_date: '2024-04-30 22:10:00'
      };
      setRecord(mockData);
      setLoading(false);
    }, 800);
  };

  useEffect(() => {
    fetchOTRecord();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete OT Log?',
      text: `Are you sure you want to remove this overtime record for ${record.employee}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: styles['swal-confirm-btn'],
        cancelButton: styles['swal-cancel-btn']
      }
    }).then((result) => {
      if (result.isConfirmed) {
        toast.success('OT record deleted');
        navigate('/employees/ot-attendance');
      }
    });
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'approved': return styles['status-approved'];
      case 'rejected': return styles['status-rejected'];
      default: return styles['status-pending'];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };

  if (loading) {
    return (
      <div className={styles['view-ot-layout']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching OT Attendance details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return <NotFound />;
  }


  return (
    <div className={styles['view-ot-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'OT Attendance', path: '/employees/ot-attendance' },
            { label: 'View Details', path: `/employees/ot-attendance/view/${id}` }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Overtime Details</h1>
              <p>Detailed punch logs and verification status for overtime shifts.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/ot-attendance')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Pencil size={18} />
                <span>Modify Log</span>
              </button>
              <button className={styles['btn-danger']} onClick={handleDelete}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          <div className={styles['ot-hero-card']}>
            <div className={styles['hero-content']}>
              <div className={styles['hero-left']}>
                <div className={styles['employee-avatar']}>{record.employee.charAt(0)}</div>
                <div className={styles['employee-info']}>
                  <div className={styles['name-status-row']}>
                    <h2>{record.employee}</h2>
                    <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                      {record.status.toUpperCase()}
                    </span>
                  </div>
                  <div className={styles['meta-row']}>
                    <div className={styles['meta-item']}>
                      <Hash size={14} /> <span>{record.employee_code}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Building2 size={14} /> <span>{record.department}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Calendar size={14} /> <span>{formatDate(record.date)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['hero-right']}>
                <div className={styles['site-location-box']}>
                  <label>Shift Location</label>
                  <div className={styles['site-value']}>
                    <MapPin size={18} />
                    <span>{record.project_site}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-grid']}>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['blue']}`}><Clock size={20} /></div>
              <div className={styles['metric-info']}>
                <label>OT Punch In</label>
                <h3>{record.ot_punch_in}</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['red']}`}><Clock size={20} /></div>
              <div className={styles['metric-info']}>
                <label>OT Punch Out</label>
                <h3>{record.ot_punch_out}</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['green']}`}><Clock9 size={20} /></div>
              <div className={styles['metric-info']}>
                <label>Total OT Hours</label>
                <h3>{record.ot_hours}h</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['orange']}`}><ShieldCheck size={20} /></div>
              <div className={styles['metric-info']}>
                <label>Payment Status</label>
                <h3>{record.status === 'approved' ? 'Verified' : 'Pending'}</h3>
              </div>
            </div>
          </div>

          <div className={styles['details-grid']}>
            <div className={styles['main-column']}>
              <div className={styles['details-card']}>
                <div className={styles['card-header']}>
                  <Activity size={20} />
                  <h3>Shift Breakdown & Reason</h3>
                </div>
                <div className={styles['info-grid']}>
                  <div className={styles['info-item']}>
                    <label>Assigned By</label>
                    <span>{record.assigned_by}</span>
                  </div>
                  <div className={styles['info-item']}>
                    <label>Shift Date</label>
                    <span>{record.date}</span>
                  </div>
                </div>
                <div className={styles['reason-box']}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', fontStyle: 'normal' }}>Work Description / Justification</label>
                  "{record.reason}"
                </div>
              </div>
            </div>

            <div className={styles['side-column']}>
              <div className={styles['details-card']}>
                <div className={styles['card-header']}>
                  <UserCheck size={20} />
                  <h3>Verification Log</h3>
                </div>
                <div className={styles['compliance-list']}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Verified By</label>
                    <span style={{ fontWeight: 600, color: '#334155' }}>{record.verified_by || 'Not Verified'}</span>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Verification Time</label>
                    <span style={{ fontWeight: 600, color: '#334155' }}>{record.verification_date || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </main>
      </div>

      <OTAttendanceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchOTRecord();
        }}
        record={record}
      />
    </div>
  );
};

export default ViewOTAttendance;
